/**
 * BJCP 2021 provisional styles — the X series.
 *
 * The BJCP's mechanism for styles that arrive between guideline editions.
 * They sit outside the 34 numbered categories, are numbered X1 upward with no
 * sub-style letter, and may be folded into a category or dropped when the next
 * edition lands — so they are a separate file and carry their own `guideline`
 * value rather than being mixed into the numbered set.
 *
 * Vital statistics are BJCP's. The prose is ours, as everywhere else in this
 * package: the guidelines' own wording is not reproduced.
 */

import type { Style } from '../../schema/style';

/** BJCP 2021 provisional (X) styles. @see Style */
export const provisionalStyles: readonly Style[] = [
  {
    id: 'bjcp-2021-x1',
    guideline: 'bjcp-2021-provisional',
    categoryNumber: 1,
    categoryLetter: null,
    name: 'Dorada Pampeana',
    categoryName: 'Provisional Styles',
    vitals: {
      og: { min: 1.042, max: 1.054 },
      fg: { min: 1.009, max: 1.013 },
      ibu: { min: 15, max: 22 },
      srm: { min: 3, max: 5 },
      abv: { min: 4.3, max: 5.5 },
    },
    overallImpression:
      "Argentina's own pale ale, worked out by homebrewers with what they could get: pilsner malt, Cascade, and a dry ale yeast. It leads with soft malt sweetness and keeps the hops to a mild flavor and a restrained bitterness, which is what separates it from an American pale ale of the same color.",
    commercialExamples: [],
    tags: ['ale', 'pale', 'argentine', 'malty', 'sessionable'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-x2',
    guideline: 'bjcp-2021-provisional',
    categoryNumber: 2,
    categoryLetter: null,
    name: 'IPA Argenta',
    categoryName: 'Provisional Styles',
    vitals: {
      og: { min: 1.055, max: 1.065 },
      fg: { min: 1.008, max: 1.015 },
      ibu: { min: 35, max: 65 },
      srm: { min: 6, max: 15 },
      abv: { min: 5.0, max: 6.5 },
    },
    overallImpression:
      'The Argentine take on an IPA: hoppy and firmly bitter but moderate in strength, and amber rather than pale. Brewed to be refreshing at the rates people actually drink it, which keeps it below the alcohol most American IPAs reach for.',
    commercialExamples: ['Antares IPA Argenta', 'Kerze IPA Argenta'],
    tags: ['ipa', 'hoppy', 'argentine', 'bitter'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-x3',
    guideline: 'bjcp-2021-provisional',
    categoryNumber: 3,
    categoryLetter: null,
    name: 'Italian Grape Ale',
    categoryName: 'Provisional Styles',
    vitals: {
      og: { min: 1.045, max: 1.1 },
      fg: { min: 1.005, max: 1.015 },
      ibu: { min: 6, max: 30 },
      srm: { min: 4, max: 25 },
      abv: { min: 4.5, max: 12.0 },
    },
    overallImpression:
      'Beer brewed with grapes, and the widest vital statistics in the guidelines because of it: anything from a 4.5 % refresher to a 12 % barrel project, pale straw to deep red. What holds it together is that the grape has to read as grape without the result becoming wine.',
    commercialExamples: [
      'Montegioco Open Mind',
      'Barley BB5-10',
      'Birrificio del Forte Il Tralcio',
      'CRAK IGA Cabernet',
    ],
    tags: ['ale', 'italian', 'grape', 'wine', 'specialty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-x4',
    guideline: 'bjcp-2021-provisional',
    categoryNumber: 4,
    categoryLetter: null,
    name: 'Catharina Sour',
    categoryName: 'Provisional Styles',
    vitals: {
      og: { min: 1.039, max: 1.048 },
      fg: { min: 1.004, max: 1.012 },
      ibu: { min: 2, max: 8 },
      srm: { min: 2, max: 6 },
      abv: { min: 4.0, max: 5.5 },
    },
    overallImpression:
      'A kettle-soured wheat beer from Santa Catarina, built around fresh fruit rather than barrel character. Clean lactic acidity, almost no bitterness at 2 to 8 IBU, and light enough in body and alcohol to drink in the heat it was invented for.',
    commercialExamples: [
      'Armada Daenerys',
      'Blumenau Catharina Sour Pessego',
      'Istepo Goiabera',
      'UNIKA Tangerina Clemenules',
    ],
    tags: ['sour', 'fruit', 'brazilian', 'wheat', 'refreshing'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-x5',
    guideline: 'bjcp-2021-provisional',
    categoryNumber: 5,
    categoryLetter: null,
    name: 'New Zealand Pilsner',
    categoryName: 'Provisional Styles',
    vitals: {
      og: { min: 1.044, max: 1.056 },
      fg: { min: 1.009, max: 1.014 },
      ibu: { min: 25, max: 45 },
      srm: { min: 2, max: 6 },
      abv: { min: 4.5, max: 5.8 },
    },
    overallImpression:
      'A pale, dry, cleanly fermented lager that exists to show off New Zealand hops — the tropical, citrusy, grassy character of Motueka, Riwaka and Nelson Sauvin over a pilsner base. Hoppier in flavor than a German pilsner without the herbal, noble edge, and the fruit is the point rather than a flaw.',
    commercialExamples: [],
    tags: ['lager', 'pilsner', 'new-zealand', 'hoppy', 'tropical'],
    provenance: 'spec-sheet',
  },
];
